import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Observable } from "rxjs";

import { UrlConstantes } from "../utilerias/constantes/urlconstantes";
import {
  AlcanceSeguimiento,
  PlanImportacionVacaciones,
  Saldo,
  Vacacion,
  VacacionReq,
  VacacionRes,
} from "../shared/model/vacaciones/vacacion.model";

@Injectable({
  providedIn: "root",
})
export class VacacionesService {
  constructor(private http: HttpClient) {}

  /**
   * Los periodos que tocan el rango, más los colaboradores con su color.
   * Van en la misma respuesta para pintar el mes con una sola llamada.
   */
  consultar(desde: string, hasta: string): Observable<VacacionRes> {
    const req: VacacionReq = { desde, hasta };
    return this.http.post<VacacionRes>(UrlConstantes.VACACIONES_CONSULTAR, req);
  }

  guardar(vacacion: Vacacion): Observable<VacacionRes> {
    const req: VacacionReq = { vacacion };
    return this.http.post<VacacionRes>(UrlConstantes.VACACIONES_GUARDAR, req);
  }

  eliminar(idVacacion: number): Observable<VacacionRes> {
    const req: VacacionReq = { idVacacion };
    return this.http.post<VacacionRes>(UrlConstantes.VACACIONES_ELIMINAR, req);
  }

  /**
   * Da de alta o corrige un periodo de alguien: se manda el `anio` DEL
   * RENGLÓN (el año en que empieza su periodo), nunca el del mes que se ve.
   * Con desde/hasta el servidor contesta la misma lista que la pantalla
   * estaba viendo (los periodos vigentes a esa fecha), no otra.
   */
  guardarSaldo(saldo: Saldo, desde?: string, hasta?: string): Observable<VacacionRes> {
    // Lo que la pantalla no escribe no viaja: el periodo, lo acumulado, lo
    // gozado fuera de COS y la fecha de corte son de la carga del Excel.
    // (El servidor igual los ignora.)
    const {
      diasArrastre, arrastreCalculado, diasGozadosPrevios, periodoInicio, periodoFin,
      fechaCorte, porAniversario, vigente, restantes, disponibles, ...limpio
    } = saldo;
    const req: VacacionReq = { saldo: limpio, desde, hasta };
    return this.http.post<VacacionRes>(
      UrlConstantes.VACACIONES_SALDO_GUARDAR,
      req,
    );
  }

  /**
   * Los periodos de un colaborador que se corrigen en «Días de vacaciones»
   * (20/10/2026): el anterior al vigente, el vigente y los que siguen, en
   * `saldos`. Sólo quien administra el calendario.
   */
  periodosDe(idUsuario: number): Observable<VacacionRes> {
    const req: VacacionReq = { saldo: { idUsuario } };
    return this.http.post<VacacionRes>(UrlConstantes.VACACIONES_SALDO_PERIODOS, req);
  }

  /* ---------- Solicitud y autorización ---------- */

  /**
   * El colaborador pide sus días.
   *
   * No se manda de quién son: el backend lo fuerza al usuario de la
   * sesión, para que nadie pueda pedir a nombre de otro.
   */
  solicitar(vacacion: Vacacion): Observable<VacacionRes> {
    const req: VacacionReq = { vacacion };
    return this.http.post<VacacionRes>(UrlConstantes.VACACIONES_SOLICITAR, req);
  }

  /**
   * Días sueltos (15/10/2026): los tramos de UN pedido, juntos y con una sola
   * firma. El servidor guarda todos o ninguno, y responde los ids en orden.
   */
  solicitarPedido(tramos: Vacacion[], firma: string): Observable<VacacionRes> {
    const req: VacacionReq = { tramos, firma };
    return this.http.post<VacacionRes>(UrlConstantes.VACACIONES_SOLICITAR_PEDIDO, req);
  }

  /**
   * Lo del propio colaborador: sus vacaciones (las que terminan del 1 de
   * enero de ese año en adelante, así salen también las del año que entra)
   * y sus periodos en `saldos`: el vigente primero, luego el que sigue. La
   * pantalla usa el que contiene la fecha «desde» que se elige.
   */
  mias(anio: number): Observable<VacacionRes> {
    const req: VacacionReq = { desde: `${anio}-01-01`, hasta: `${anio}-12-31` };
    return this.http.post<VacacionRes>(UrlConstantes.VACACIONES_MIAS, req);
  }

  /**
   * Autoriza o rechaza. El motivo solo se manda al rechazar; la firma,
   * solo al autorizar — sella el FRC-019.
   */
  resolver(
    idVacacion: number,
    aprobar: boolean,
    motivo?: string,
    firma?: string
  ): Observable<VacacionRes> {
    const req: VacacionReq = { idVacacion, aprobar, motivo, firma };
    return this.http.post<VacacionRes>(UrlConstantes.VACACIONES_RESOLVER, req);
  }

  /** Los datos completos (con firmas) para armar el FRC-019. */
  frc(idVacacion: number): Observable<VacacionRes> {
    const req: VacacionReq = { idVacacion };
    return this.http.post<VacacionRes>(UrlConstantes.VACACIONES_FRC, req);
  }

  /** Sube el FRC-019 firmado a mano (PDF, JPG o PNG). */
  subirFirmado(idVacacion: number, archivo: File): Observable<VacacionRes> {
    const forma = new FormData();
    forma.append("file", archivo, archivo.name);
    forma.append("idVacacion", String(idVacacion));
    return this.http.post<VacacionRes>(UrlConstantes.VACACIONES_FIRMADO_SUBIR, forma);
  }

  /** Los bytes del FRC-019 firmado, por HttpClient para que viaje el token. */
  verFirmado(idVacacion: number, idFirmado?: number): Observable<Blob> {
    const req: VacacionReq = { idVacacion, idFirmado };
    return this.http.post(UrlConstantes.VACACIONES_FIRMADO_VER, req, { responseType: "blob" });
  }

  /** Los FRC-019 firmados que se han subido a la solicitud, del último al primero. */
  historialFirmado(idVacacion: number): Observable<VacacionRes> {
    const req: VacacionReq = { idVacacion };
    return this.http.post<VacacionRes>(UrlConstantes.VACACIONES_FIRMADO_HISTORIAL, req);
  }

  /**
   * El colaborador retira una solicitud suya que aún nadie resuelve.
   *
   * No se manda de quién es: el backend lo fuerza al usuario de la
   * sesión, igual que al pedir.
   */
  cancelarMia(idVacacion: number): Observable<VacacionRes> {
    const req: VacacionReq = { idVacacion };
    return this.http.post<VacacionRes>(UrlConstantes.VACACIONES_CANCELAR_MIA, req);
  }

  /**
   * Seguimiento: todo lo del año con su rastro.
   *
   * Distinto de consultar(): ahí solo vienen las vigentes y las que
   * esperan; aquí salen también las rechazadas y las retiradas, que es
   * justo lo que se viene a revisar.
   */
  historial(anio: number): Observable<VacacionRes> {
    const req: VacacionReq = { desde: `${anio}-01-01`, hasta: `${anio}-12-31` };
    return this.http.post<VacacionRes>(UrlConstantes.VACACIONES_HISTORIAL, req);
  }

  /**
   * El seguimiento de solo lectura: vacaciones, permisos y personas del
   * año, acotados al alcance que el servidor conceda. "Mi equipo" pide
   * EQUIPO; "Seguimiento RH" pide TODOS. La respuesta trae en `alcance`
   * lo que de verdad se concedió.
   */
  seguimiento(anio: number, alcance: AlcanceSeguimiento): Observable<VacacionRes> {
    const req: VacacionReq = {
      desde: `${anio}-01-01`,
      hasta: `${anio}-12-31`,
      alcance,
    };
    return this.http.post<VacacionRes>(UrlConstantes.VACACIONES_SEGUIMIENTO, req);
  }

  /* ---------- Carga del Excel de RH ----------
   * Dos pasos, calcado del formato FOS-005 (matriz-riesgo.service.ts):
   * la previa NO escribe nada y sirve para revisar fila por fila; sólo
   * al confirmar se guardan los saldos. El archivo viaja las dos veces
   * a propósito, para no dejar el .xlsx a medias en el servidor entre
   * una llamada y la otra. */

  /**
   * El multipart de las dos llamadas.
   *
   * Sin Content-Type a mano: si se pone, el navegador ya no calcula el
   * boundary y el servidor no encuentra el archivo.
   */
  private formaImportacion(
    archivo: File,
    fechaCorte?: string | null,
    hoja?: string | null,
    asignaciones?: string | null,
    huella?: string,
    filas?: number[],
  ): FormData {
    const f = new FormData();
    f.append("file", archivo, archivo.name);
    // La FECHA DE LA HOJA (yyyy-MM-dd): cada persona va al periodo de
    // aniversario vigente a esa fecha. Sin ella, el servidor usa la fecha en
    // que se guardó el libro. La carga manda la MISMA que se revisó.
    if (fechaCorte) {
      f.append("fechaCorte", fechaCorte);
    }
    // La hoja del libro que se lee (nombre exacto). Sin ella, el servidor
    // toma la única que trae la tabla; si trae varias, pide elegir.
    if (hoja) {
      f.append("hoja", hoja);
    }
    // Las filas asignadas a mano: "fila:idUsuario,fila:idUsuario". Van en la
    // huella, así que la carga manda las mismas de la previa.
    if (asignaciones) {
      f.append("asignaciones", asignaciones);
    }
    // Se compara contra null y no por verdadero: en la carga la huella es
    // obligatoria para el servidor, y si viniera vacía hay que mandarla
    // vacía para que conteste "no coincide" en vez de un 400 sin
    // explicación. En la previa no se manda, que es lo correcto.
    if (huella != null) {
      f.append("huella", huella);
    }
    // Los renglones del Excel que se eligieron, separados por coma.
    //
    // Sólo se manda si trae algo: para el servidor "filas" ausente o
    // vacío significa "todas las cargables", que es como se comportaba
    // antes de que se pudiera elegir. Mandar la cadena vacía sería
    // pedirle que adivine, así que ni se manda.
    if (filas && filas.length) {
      f.append("filas", filas.join(","));
    }
    return f;
  }

  /** Lee el Excel y devuelve lo que se cargaría. No escribe nada. */
  previaImportacion(
    archivo: File,
    fechaCorte?: string | null,
    hoja?: string | null,
    asignaciones?: string | null,
  ): Observable<PlanImportacionVacaciones> {
    return this.http.post<PlanImportacionVacaciones>(
      UrlConstantes.VACACIONES_IMPORTAR_PREVIA,
      this.formaImportacion(archivo, fechaCorte, hoja, asignaciones),
    );
  }

  /**
   * Aplica el plan, todo o nada. La huella es la de la previa: si el
   * archivo cambió entre una y otra, el servidor la rechaza en vez de
   * cargar algo distinto de lo que se revisó.
   *
   * `filas` son los renglones del Excel que se palomearon en la vista
   * previa. Sin ellos se cargan todas las cargables, como siempre; con
   * ellos sólo esos se escriben y el resto vuelve marcado como OMITIDA.
   * Así se puede cerrar un área primero y dejar el resto para después.
   *
   * `hoja` y `asignaciones` son las MISMAS que se mandaron en la previa
   * revisada: van en la huella, y con otras se rechaza.
   */
  importarExcel(
    archivo: File,
    fechaCorte: string | null | undefined,
    hoja: string | null | undefined,
    asignaciones: string | null | undefined,
    huella: string,
    filas?: number[],
  ): Observable<PlanImportacionVacaciones> {
    return this.http.post<PlanImportacionVacaciones>(
      UrlConstantes.VACACIONES_IMPORTAR_CARGAR,
      this.formaImportacion(archivo, fechaCorte, hoja, asignaciones, huella, filas),
    );
  }
}
